import type { CliAction } from "@/store/chat-types";

export interface StudioReleaseDeterministicIntent {
  text: string;
  /**
   * At most one — this intent is narrated by `text` alone through
   * `runClientDeterministicIntent`. See `SingleActionIntentResponse`
   * (mock-chat-service.ts) for why a longer list cannot be told truthfully.
   */
  actions?: [] | [CliAction];
}

function releaseIdFromPrompt(prompt: string): string | null {
  const labelled = prompt.match(
    /(?:release(?:\s*id)?|releaseId|릴리스(?:\s*(?:id|아이디))?)\s*(?:=|:|is|은|는)?\s*["“”'`]?([a-z0-9][a-z0-9._:-]*)["“”'`]?/i,
  )?.[1];
  return labelled?.trim() || null;
}

function previewVisibility(prompt: string): "public" | "unlisted" | null {
  if (/\bunlisted\b|비공개\s*링크|링크\s*전용/i.test(prompt)) return "unlisted";
  if (/\bpublic\b|공개/i.test(prompt)) return "public";
  return null;
}

/**
 * Resolve only release requests whose bounded tool and required identifiers
 * are unambiguous. Deploy and rollback deliberately stop at grounded guidance:
 * their executor inputs must come from the canonical release projection and
 * still pass the capability gateway's typed confirmation.
 */
export function buildStudioReleaseDeterministicIntent(
  content: string,
): StudioReleaseDeterministicIntent | null {
  const prompt = content.trim();
  const mentionsRelease =
    /\brelease\b|\bpublication\b|릴리스|게시\s*후보|배포\s*후보/i.test(prompt);

  if (
    /(?:share|공유).*(?:preview|프리뷰)|(?:preview|프리뷰).*(?:share|공유)/i.test(
      prompt,
    )
  ) {
    const previewUrl = prompt.match(/https:\/\/[^\s"'`]+/i)?.[0] ?? null;
    const visibility = previewVisibility(prompt);
    if (!previewUrl || !visibility) {
      return {
        text: "Preview sharing needs the exact HTTPS preview URL and visibility (public or unlisted). A share record is not live deployment proof.",
      };
    }
    return {
      text: `Sharing ${previewUrl} as ${visibility} is ready for confirmation. Studio will retain share evidence without claiming live deployment.`,
      actions: [
        {
          type: "cogo.studio.share_cogo_preview",
          payload: { previewUrl, visibility },
        },
      ],
    };
  }

  if (
    /\brollback\b|롤백|이전\s*배포로\s*(?:복원|되돌)/i.test(prompt) &&
    (mentionsRelease || /배포/i.test(prompt))
  ) {
    return {
      text: "Rollback is not inferred from a version name or free text. First inspect the canonical release, choose an exact server-authorized same-environment receipt, and provide its ref/hash plus the candidate and target output hash. Studio then requires typed confirmation and refreshes live readback.",
    };
  }

  if (
    /\b(?:deploy|deployment)\b|\bstaging\b|\bproduction\b|스테이징|프로덕션|운영\s*배포/i.test(
      prompt,
    ) &&
    mentionsRelease
  ) {
    return {
      text: "Release deployment is not executed from natural-language inference. Resolve the exact release, environment, adapter, candidate/output hashes, and matching one-time grant or authorization first. Studio will then require typed confirmation; completion still depends on receipt and live-server readback.",
    };
  }

  if (
    /\b(?:readback|live[- ]server)\b|라이브\s*서버|배포\s*(?:결과|상태)\s*(?:확인|새로고침)/i.test(
      prompt,
    ) &&
    (mentionsRelease || /배포/i.test(prompt))
  ) {
    const releaseId = releaseIdFromPrompt(prompt);
    if (!releaseId) {
      return {
        text: "Publication readback needs the exact canonical release ID. It reports only returned receipt and live-server hash observations.",
      };
    }
    return {
      text: `Refreshing artifact and live-server readback for release ${releaseId}.`,
      actions: [
        {
          type: "cogo.studio.refreshPublicationReadback",
          payload: { releaseId },
        },
      ],
    };
  }

  if (
    /\bverif(?:y|ication)\b|검증/i.test(prompt) &&
    (mentionsRelease || /배포/i.test(prompt))
  ) {
    const releaseId = releaseIdFromPrompt(prompt);
    if (!releaseId) {
      return {
        text: "Publication verification needs the exact canonical release ID. Chat cannot supply or inject passing proof.",
      };
    }
    return {
      text: `Verification for release ${releaseId} is ready for confirmation. Missing proof will remain not-proven.`,
      actions: [
        {
          type: "cogo.studio.runPublicationVerification",
          payload: { releaseId },
        },
      ],
    };
  }

  return null;
}
